import React from 'react';
import useHydrated from '../hooks/useHydrated';
import ContextWeb from './ContextWeb';
import ScreenplaySnap from './ScreenplaySnap';
import StoryboardRun from './StoryboardRun';
import './ModernHero.css';
import virgilProfileMark from '../assets/virgil-profile-mark.webp';
import virgilIntroPoster from '../assets/virgil-intro-poster.jpg';
import virgilHeroFilmPoster from '../assets/virgil-hero-film-poster.webp';

const HERO_FEATURES = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="7" y="8" width="12" height="14" rx="3.5" fill="rgba(240, 139, 91, 0.12)" stroke="#F08B5B" strokeWidth="1.4"/>
        <path d="M10 12h6M10 15.5h8M10 19h5" stroke="#F0BB8A" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M11 6h10a3 3 0 0 1 3 3v10" stroke="#F08B5B" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Context that holds.',
    sub: 'Large drafts, character webs, tone notes, scene fragments, and research stay close at hand, so you can keep making decisions with the whole story in view.',
    graphic: ContextWeb,
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 5h7l4 4v10a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9a4 4 0 0 1 4-4Z" fill="rgba(226, 184, 74, 0.1)" stroke="#E2B84A" strokeWidth="1.4"/>
        <path d="M16 5v4h4" stroke="#E2B84A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9.5 14h9M9.5 17.5h9M9.5 21h6" stroke="#F0D48A" strokeWidth="1.8" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Format-native craft.',
    sub: 'Screenplay, pilot, novel, treatment, podcast, pitch deck, or coverage pass. Virgil adapts the surface to the kind of work you are actually doing.',
    graphic: ScreenplaySnap,
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 20L20 8" stroke="#F3B15E" strokeWidth="2" strokeLinecap="round"/>
        <path d="M14 8h6v6" stroke="#F3B15E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="8" cy="9" r="1.5" fill="#FFD79E"/>
        <circle cx="11.5" cy="6" r="1" fill="#FFD79E"/>
        <circle cx="20" cy="19.5" r="1.5" fill="#FFD79E"/>
      </svg>
    ),
    title: 'Generative momentum.',
    sub: 'Draft scenes, punch dialogue, map beats, generate storyboards, and iterate visuals without jumping between disconnected tools.',
    graphic: StoryboardRun,
  }
];

const HERO_BYLINES = [
  "Creativity is an inferno. Let Virgil guide you to paradise.",
  "From blank page to locked pages, without losing your voice.",
  "Screenplays, novels, podcasts, pitches, and visual beats in one flow.",
];

const HERO_FORMATS = ['Screenplays', 'Novels', 'Pitches', 'Podcasts', 'Coverage'];
const HERO_FILMS = [
  {
    src: '/media/virgil-website-intro.mp4?v=2',
    poster: virgilIntroPoster,
  },
  {
    src: '/media/virgil-hero-film.mp4',
    poster: virgilHeroFilmPoster,
  },
];

const ModernHero = ({ children, sectionId, sectionRef, className = '' }) => {
  const hydrated = useHydrated();
  const [bylineIndex, setBylineIndex] = React.useState(0);
  const [visible, setVisible] = React.useState(true);
  const bylineMeasureRef = React.useRef(null);
  const [bylineHeight, setBylineHeight] = React.useState(null);
  const artVideoRef = React.useRef(null);
  const [artAwake, setArtAwake] = React.useState(false);
  const [artMuted, setArtMuted] = React.useState(true);
  const [activeFilmIndex, setActiveFilmIndex] = React.useState(0);
  const activeFilm = HERO_FILMS[activeFilmIndex];

  // The plate opens on a frame from the film, then quietly comes alive.
  React.useEffect(() => {
    if (!artVideoRef.current || typeof window === 'undefined') return undefined;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setArtAwake(true);
    }, 1150);

    return () => window.clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    const video = artVideoRef.current;
    if (!video || !artAwake) return;

    video.load();
    video.play().catch(() => {});
  }, [activeFilmIndex, artAwake]);
  React.useEffect(() => {
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setBylineIndex((i) => (i + 1) % HERO_BYLINES.length);
        setVisible(true);
      }, 600);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      const aurora = document.querySelector('.hero-aurora');
      if (aurora) {
        const x = (e.clientX / window.innerWidth - 0.5) * 20;
        const y = (e.clientY / window.innerHeight - 0.5) * 20;
        aurora.style.transform = `translate(${x}px, ${y}px)`;
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  React.useEffect(() => {
    const measure = () => {
      const node = bylineMeasureRef.current;
      if (!node) return;

      const heights = Array.from(node.children).map((child) => Math.ceil(child.getBoundingClientRect().height));
      const nextHeight = Math.max(0, ...heights);

      if (nextHeight > 0) {
        setBylineHeight(nextHeight);
      }
    };

    measure();

    if (typeof ResizeObserver !== 'undefined' && bylineMeasureRef.current) {
      const observer = new ResizeObserver(measure);
      observer.observe(bylineMeasureRef.current);
      Array.from(bylineMeasureRef.current.children).forEach((child) => observer.observe(child));

      return () => observer.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const toggleArtSound = () => {
    const video = artVideoRef.current;
    if (!video) return;

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setArtMuted(nextMuted);
    video.play().catch(() => {});
  };

  return (
    <section
      id={sectionId}
      ref={sectionRef}
      className={`modern-hero ${children ? 'with-showcase' : ''} ${className}`.trim()}
    >
      <div className="hero-layout">
        <div className="hero-content">
          <div className="hero-copy">
            <div className="hero-title-row fade-seq-1">
              <div className="hero-medallion hero-medallion--solo" role="img" aria-label="Virgil">
                <img
                  src={virgilProfileMark}
                  alt=""
                  className="hero-medallion-face hero-medallion-profile"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  aria-hidden="true"
                />
              </div>
              <h1 className="hero-heading">
                <span className="hero-heading-line">Meet</span>
                <span className="heading-gradient">Virgil</span>
              </h1>
            </div>
            <span className="hero-kicker fade-seq-2">
              <span className="hero-kicker-dot" aria-hidden="true" />
              Your Storytelling OS
            </span>
            
            <div
              className="hero-byline-container fade-seq-3"
              style={bylineHeight ? { minHeight: `${bylineHeight}px` } : undefined}
            >
              <div className="hero-byline-measure" ref={bylineMeasureRef} aria-hidden="true">
                {HERO_BYLINES.map((byline) => (
                  <p key={byline} className="hero-tagline hero-tagline-measure">
                    {byline}
                  </p>
                ))}
              </div>
              <p 
                className="hero-tagline hero-tagline-live"
                key={bylineIndex}
                style={{
                  animation: visible ? 'bylineIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'bylineOut 0.5s cubic-bezier(0.7, 0, 0.84, 0) forwards'
                }}
              >
                {HERO_BYLINES[bylineIndex]}
              </p>
            </div>

            <div className="hero-cta fade-seq-3">
              <button
                className="btn btn-primary"
                onClick={() => (window.location.href = '/signup')}
              >
                Start your 14-day trial
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => (window.location.href = '/download')}
              >
                Download
              </button>
            </div>

            <div className="hero-formats fade-seq-4" aria-label="What you can write in Virgil">
              {HERO_FORMATS.map((format, i) => (
                <React.Fragment key={format}>
                  {i > 0 && <span className="hero-formats-dot" aria-hidden="true" />}
                  <span className="hero-format">{format}</span>
                </React.Fragment>
              ))}
            </div>

            <div className="hero-models fade-seq-5">
              <div className="model-carousel" aria-label="Model providers">
                <div className="model-carousel-track">
                  {[1,2,3].map(n => (
                    <React.Fragment key={n}>
                      <span className="model-badge">Claude Opus 5.5</span>
                      <span className="model-badge">GPT-6.1 Sol Ultra</span>
                      <span className="model-badge">Gemini 3.1 Pro</span>
                      <span className="model-badge">Grok 4.7</span>
                      <span className="model-badge">DeepSeek V4 Pro</span>
                      <span className="model-badge">Kimi K3</span>
                      <span className="model-sep">/</span>
                      <span className="model-badge media">Seedance 2.5</span>
                      <span className="model-badge media">Gemini Omni 1.1 Flash</span>
                      <span className="model-badge media">Veo 3.1</span>
                      <span className="model-badge media">Kling 3.0 Turbo</span>
                      <span className="model-badge media">MiniMax H3</span>
                      <span className="model-badge media">Luma Ray2</span>
                      <span className="model-badge media">Runway Gen-4.5</span>
                      <span className="model-badge media">FLUX 3 Video</span>
                      <span className="model-badge media">FLUX 3 Image</span>
                      <span className="model-badge media">Nano Banana 2.1</span>
                      <span className="model-badge media">Seedream 5.0 Pro</span>
                      <span className="model-badge media">Seedream 5.0 Flash</span>
                      <span className="model-badge media">Recraft V4.1</span>
                      <span className="model-badge media">Ideogram 4.5 Precise Edit</span>
                      <span className="model-badge media">Gemini 3.8 Flash TTS</span>
                      <span className="model-badge media">Eleven v4</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div className="model-carousel-copy">
                All writing, image, video, and voice models shown here are available in the public beta.
              </div>
            </div>
          </div>

          <div className={`hero-art-aperture ${artAwake ? 'is-awake' : ''}`}>
            {/* The film starts downloading after hydration, so it does not
                compete with the page's own CSS, fonts, and script. */}
            <video
              ref={artVideoRef}
              className="hero-art-aperture-image"
              muted={artMuted}
              playsInline
              preload={hydrated ? 'auto' : 'none'}
              poster={activeFilm.poster}
              src={hydrated ? activeFilm.src : undefined}
              onEnded={() => {
                setActiveFilmIndex((index) => (index + 1) % HERO_FILMS.length);
              }}
              tabIndex={-1}
              aria-hidden="true"
            />
            {/* A frame from the film dissolves away as playback begins. */}
            <img
              src={virgilIntroPoster}
              alt=""
              className="hero-art-aperture-image hero-art-aperture-still"
              loading="eager"
              decoding="async"
              fetchpriority="high"
              aria-hidden="true"
            />
          </div>

          {/* Anchored to the copy row on desktop (over the backdrop film)
              and to the plate's corner on smaller layouts. */}
          {artAwake && (
            <button
              type="button"
              className="hero-art-sound-toggle"
              onClick={toggleArtSound}
              aria-label={artMuted ? 'Turn video sound on' : 'Mute video'}
              aria-pressed={!artMuted}
              title={artMuted ? 'Turn sound on' : 'Mute'}
            >
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <path d="M3 8h3l4-3v10l-4-3H3V8Z" />
                {artMuted ? (
                  <path d="m13.5 7 4 6m0-6-4 6" />
                ) : (
                  <path d="M13 7.25c1.45 1.5 1.45 4 0 5.5m2-7.5c2.55 2.6 2.55 6.9 0 9.5" />
                )}
              </svg>
            </button>
          )}
        </div>

        {children && (
          <div className="hero-showcase">
            <div className="hero-showcase-inner">
              {children}
            </div>
          </div>
        )}

        <div className="hero-bottom">
          <HeroSideRail />
        </div>
      </div>
    </section>
  );
};

export default ModernHero;

function HeroSideRail() {
  return (
    <aside className="hero-rail" aria-label="Virgil highlights">
      <div className="hero-rail-label">What the studio handles</div>
      {HERO_FEATURES.map((item) => (
        <div className="hero-rail-item" key={item.title}>
          <div className="hero-rail-icon" aria-hidden>
            {item.icon}
          </div>
          <div className="hero-rail-copy">
            <div className="hero-rail-title">{item.title}</div>
            <div className="hero-rail-sub">{item.sub}</div>
            {item.graphic && (
              <div className="hero-rail-graphic">
                <item.graphic />
              </div>
            )}
          </div>
        </div>
      ))}
    </aside>
  );
}
